import { useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import classNames from 'classnames';

import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';
import CheckoutRadioContainer from '../../shared/CheckoutRadioContainer';
import CheckoutCreditCardForm from './CheckoutCreditCardForm';
import CheckoutCreditCardView from './CheckoutCreditCardView';

import { isVerifyingCreditCard } from '../../reducers/checkoutPaymentReducer';
import { PANES, ACTIONS } from '../../constants/checkoutConstants';

import styles from './styles/CheckoutCreditCard.scss';

function CheckoutCreditCard({
    viewer,
    buyer,
    cardToken,
    isSelected,
    isVerifying,
    actions,
    onSelect,
    checkoutPaymentErrors,
    onValidationUpdate,
    onValidationError,
}) {
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const isVerifyingForm = isVerifying && isSelected;

    const props = {
        type: PANES.PANE_PAYMENT,
        serviceId: cardToken,
        isSelected,
        wrapperClass: classNames(styles.radioWrapper, {
            [styles.isMobile]: isMobile,
        }),
        actionsWrapperClass: styles.actionsWrapper,
    };
    const walletPaymentMethods = get(buyer, 'wallet.paymentMethods') || [];
    const walletPaymentMethod = walletPaymentMethods.find(
        ({ paymentMethod }) => paymentMethod.token === cardToken
    );
    const creditCard = get(walletPaymentMethod, 'paymentMethod');
    const isExpired = get(creditCard, 'isExpired');

    if (isVerifyingForm) {
        Object.assign(props, {
            label: get(creditCard, 'cardHolderName'),
            isClickable: !isSelected,
            actions: {
                [ACTIONS.ACTION_CANCEL]: actions.onCancel,
                [ACTIONS.ACTION_DELETE]: actions.onDelete,
            },
            onSelect: (value, event) => onSelect(event, value, isSelected),
        });
    } else {
        Object.assign(props, {
            label: <CheckoutCreditCardView creditCard={creditCard} hideName showTitle={isMobile} />,
            isClickable: !isExpired,
            actions: {
                [ACTIONS.ACTION_DELETE]: actions.onDelete,
            },
            onSelect: (value, event) => onSelect(event, value, isSelected),
        });
    }

    return (
        <CheckoutRadioContainer {...props}>
            {isVerifyingForm && (
                <CheckoutCreditCardForm
                    viewer={viewer}
                    buyer={buyer}
                    checkoutPaymentErrors={checkoutPaymentErrors}
                    onValidationUpdate={onValidationUpdate}
                    onValidationError={onValidationError}
                />
            )}
        </CheckoutRadioContainer>
    );
}

CheckoutCreditCard.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    cardToken: PropTypes.string.isRequired,
    isVerifying: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    checkoutPaymentErrors: PropTypes.object.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    onValidationError: PropTypes.func.isRequired,
};

const ConnectedCheckoutCreditCard = connect(({ payment }) => ({
    isVerifying: isVerifyingCreditCard(payment),
}))(CheckoutCreditCard);

export default createFragmentContainer(ConnectedCheckoutCreditCard, {
    viewer: graphql`
        fragment CheckoutCreditCard_viewer on Viewer {
            ...CheckoutCreditCardForm_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutCreditCard_buyer on User {
            ...CheckoutCreditCardForm_buyer
            wallet {
                paymentMethods {
                    paymentMethod {
                        ...CheckoutCreditCardView_creditCard
                        __typename
                        ... on CreditCard {
                            token
                            isExpired
                            cardHolderName
                            cardExpiryMonth
                            cardExpiryYear
                            maskedCardNumber
                        }
                    }
                    shippingAddresses {
                        serviceId
                    }
                }
            }
        }
    `,
});
